import React from 'react';
import { Trophy, Zap, Vault, Handshake, ShoppingBag } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { Link } from 'react-router-dom';
import LightSwitch from './LightSwitch';
import LotteryBadge from './LotteryBadge';
import { useTrades } from '../hooks/useTrades';
import NotificationsBell from './NotificationsBell';
import UserMenu from './UserMenu';
import TradesSheet from './TradesSheet';
import { api } from '../lib/api';
import { nextDrawDate, WEEKLY_PRIZE } from '../lib/lottery';
import { useBrandWord } from '../hooks/useBrandWord';

export default function Header({ onLotteryClick }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { trades } = useTrades();
  const brandWord = useBrandWord();
  const [remoteSettings, setRemoteSettings] = React.useState(null);
  // Poll the public settings endpoint every 30s so the badge stays in sync
  // as the community earns more tickets — no login required.
  React.useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const s = await api.getLotterySettings();
        if (!cancelled) setRemoteSettings(s);
      } catch { /* ignore */ }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, 30_000);
    // also refresh when the tab comes back into focus so mobile users get
    // an instant update instead of waiting for the next tick.
    const onVisible = () => { if (document.visibilityState === 'visible') fetchOnce(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const badgeEntries = remoteSettings?.pool_total_entries ?? 0;
  const badgePrize = remoteSettings?.weekly_prize || WEEKLY_PRIZE;
  const badgeTarget = remoteSettings?.draw_at
    ? new Date(remoteSettings.draw_at)
    : nextDrawDate(new Date());
  // Match the design's mobile breakpoint (sm = 640px in Tailwind)
  const isMobile = typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(max-width: 639px)').matches
    : false;
  const [lotterySize, setLotterySize] = React.useState(isMobile ? 150 : 220);
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 639px)');
    const on = () => setLotterySize(mq.matches ? 150 : 220);
    on();
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);

  return (
    <header className="relative">
      <div className="relative max-w-7xl mx-auto px-3 sm:px-6 py-6 sm:py-10 md:py-16">
        {/* Top row: brand + auth + theme toggle */}
        <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2 sm:gap-3 flex-wrap">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex items-center justify-center h-9 w-9 sm:h-11 sm:w-11 rounded-xl sm:rounded-2xl bg-[#ff2bd6] shadow-[0_6px_0_#a4007f,0_0_24px_rgba(255,43,214,0.7)] -rotate-6">
              <Vault className="h-5 w-5 sm:h-6 sm:w-6 text-white" strokeWidth={3} />
            </div>
            <span
              key={brandWord}
              className="font-black tracking-[0.2em] sm:tracking-[0.3em] text-[11px] sm:text-sm uppercase px-2 py-0.5 sm:px-3 sm:py-1 rounded-full border-2 shadow-[0_3px_0_var(--t-card-border)] transition-all"
              style={{
                background: isDark ? '#1a0b3d' : '#ffffff',
                color: isDark ? '#ffd900' : '#3a1064',
                borderColor: isDark ? '#ffd900' : '#3a1064',
                animation: 'brandFade 400ms ease-out',
              }}
              data-testid="header-brand-word"
            >
              {brandWord}
            </span>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">
            <Link
              to="/merch"
              data-testid="header-merch-btn"
              aria-label="Hot Merch For Sale"
              title="Hot Merch For Sale"
              className="relative h-9 sm:h-10 px-2.5 sm:px-3 rounded-lg sm:rounded-xl bg-gradient-to-r from-[#ffd900] via-[#ff9a1c] to-[#ff2bd6] border-2 border-[#0a0018] shadow-[0_3px_0_#0a0018] flex items-center gap-1.5 text-[#0a0018] font-black text-[10px] sm:text-xs uppercase tracking-wider hover:brightness-105 transition-all"
            >
              <ShoppingBag className="h-4 w-4" />
              <span className="hidden sm:inline">Hot Merch</span>
              <span
                aria-hidden="true"
                className="absolute -top-1.5 -right-1.5 h-4 min-w-[16px] px-1 rounded-full bg-[#0a0018] text-[#ffd900] text-[9px] font-black flex items-center justify-center border-2 border-white"
              >
                🔥
              </span>
            </Link>
            <TradesSheet
              trigger={
                <button
                  data-testid="header-trades-btn"
                  aria-label="Trades"
                  className="relative h-9 px-2.5 sm:h-10 sm:px-3 rounded-lg sm:rounded-xl bg-white border-2 border-[#3a1064] shadow-[0_3px_0_#3a1064] flex items-center gap-1.5 text-[#3a1064] font-black text-xs sm:text-sm hover:bg-yellow-50 transition-colors"
                >
                  <Handshake className="h-4 w-4 text-[#ff2bd6]" />
                  <span className="hidden sm:inline">Trades</span>
                </button>
              }
            />
            <NotificationsBell />
            <UserMenu />
            <LightSwitch />
          </div>
        </div>

        {/* Hero row: headline (left) + lottery sticker (right) */}
        <div className="grid md:grid-cols-[1fr_auto] gap-4 sm:gap-8 items-center">
          <div>
            <h1 className="font-black text-3xl sm:text-[2.75rem] md:text-7xl leading-[0.95] tracking-tight">
              <span className="block text-white">
                Catch &apos;em, trade &apos;em, lock &apos;em
              </span>
              <span className="block mt-1 text-[#ffd900]">
                all in the vault.
              </span>
            </h1>

            <p className="mt-3 sm:mt-5 max-w-2xl text-xs sm:text-base md:text-lg font-bold text-white">
              The complete Fortnite Sprite vault — every variant, every level. Mark what you
              <span className="bg-[#3a1064] text-yellow-300 px-1.5 mx-1 rounded">own</span>,
              rank up your <span className="bg-[#ff2bd6] text-white px-1.5 mx-1 rounded">levels</span>,
              and <span className="bg-[#00b8ff] text-white px-1.5 mx-1 rounded">master</span> the set.
            </p>

            <div className="mt-4 sm:mt-6 flex flex-wrap items-center gap-2">
              <span
                className="inline-flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs font-black px-2 sm:px-3 py-1 sm:py-1.5 rounded-full border-2 shadow-[0_3px_0_var(--t-card-border)]"
                style={{
                  background: isDark ? '#1a0b3d' : '#ffffff',
                  color: isDark ? '#ffd900' : '#3a1064',
                  borderColor: isDark ? '#ffd900' : '#3a1064',
                }}
              >
                <Trophy className="h-3.5 w-3.5 text-[#ff2bd6]" /> Unofficial fan-made tracker
              </span>
              <span
                className="inline-flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs font-black px-2 sm:px-3 py-1 sm:py-1.5 rounded-full border-2 shadow-[0_3px_0_var(--t-card-border)]"
                style={{
                  background: '#3a1064',
                  color: '#ffd900',
                  borderColor: '#3a1064',
                }}
              >
                <Zap className="h-3.5 w-3.5" fill="currentColor" /> 61 sprites · 305 levels
              </span>
            </div>
          </div>

          {/* Big lottery sticker — extra vertical padding reserves room for the
              "Win" sticker above and "13.5K" price tag below (both absolute-positioned) */}
          <div className="flex justify-center md:justify-end pr-2 md:pr-6 mt-3 md:mt-0 pt-2 pb-5 md:py-0">
            <LotteryBadge
              entries={badgeEntries}
              weeklyPrize={badgePrize}
              targetDate={badgeTarget}
              onScrollTo={onLotteryClick}
              size={lotterySize}
            />
          </div>
        </div>
      </div>
    </header>
  );
}
